import { computeTotals, modifiersPrice, type VatCode } from "@bb/domain";
import type { CatalogDTO, NewItemInput, OrderDTO, OrderItemDTO } from "@bb/protocol";
import type { OutboxEntry } from "./outbox.ts";

/**
 * Etap potwierdzenia pozycji — pokazywany obsłudze wprost:
 *  device   = zapisane tylko na tablecie (czeka na wysyłkę),
 *  accepted = serwer przyjął, kuchnia jeszcze nie dostała,
 *  kitchen_queued = w kolejce na bonownik (bramka jeszcze nie potwierdziła wydruku),
 *  kitchen_printed = bon wydrukowany w kuchni,
 *  served / voided.
 */
export type ItemStage = "device" | "accepted" | "kitchen_queued" | "kitchen_printed" | "served" | "voided";

export interface ViewItem extends OrderItemDTO {
  stage: ItemStage;
}

export interface OrderView extends Omit<OrderDTO, "items"> {
  items: ViewItem[];
  /** Zamówienie istnieje tylko na tablecie (order.create jeszcze nie dotarło). */
  local: boolean;
  pendingCount: number;
}

const STAGE: Record<OrderItemDTO["kitchenStatus"], ItemStage> = {
  new: "accepted",
  queued: "kitchen_queued",
  printed: "kitchen_printed",
  served: "served",
  voided: "voided",
};

/** Buduje pozycję „na tablecie” z komendy w outboxie, licząc cenę z lokalnego katalogu (serwer i tak przeliczy). */
export function itemFromInput(input: NewItemInput, catalog: CatalogDTO | null): ViewItem {
  const p = catalog?.products.find((x) => x.id === input.productId);
  const v = p?.variants.find((x) => x.id === input.variantId);
  const groups = catalog?.modifierGroups.filter((g) => p?.modifierGroupIds.includes(g.id)) ?? [];
  const mods = (input.modifiers ?? []).map((m) => {
    const g = groups.find((x) => x.modifiers.some((y) => y.id === m.modifierId));
    const mod = g?.modifiers.find((x) => x.id === m.modifierId);
    return { modifierId: m.modifierId, groupId: g?.id ?? "", name: mod?.name ?? "?", price: mod?.price ?? 0, qty: m.qty };
  });
  const unit = (v?.price ?? p?.price ?? 0) + modifiersPrice(mods, (gid) => groups.find((g) => g.id === gid)?.freeQty ?? 0);
  return {
    id: input.itemId,
    productId: input.productId,
    variantId: input.variantId ?? null,
    name: v?.itemName || p?.name || "?",
    variantName: v?.name ?? null,
    unitPrice: unit,
    qty: input.qty,
    vatCode: (p?.vatCode ?? "A") as VatCode,
    vatPercent: p?.vatPercent ?? null,
    notes: input.notes ?? null,
    modifiers: mods.map(({ groupId: _, ...m }) => m),
    kitchenStatus: "new",
    discount: null,
    guestNo: input.guestNo ?? null,
    voidReason: null,
    addedBy: null,
    createdAt: new Date().toISOString(),
    stage: "device",
  };
}

/** Łączy stan z serwera z komendami czekającymi w outboxie tabletu. */
export function buildOrderViews(orders: OrderDTO[], pending: readonly OutboxEntry[], catalog: CatalogDTO | null): OrderView[] {
  const views = new Map<string, OrderView>();
  for (const o of orders) {
    views.set(o.id, { ...o, items: o.items.map((i) => ({ ...i, stage: STAGE[i.kitchenStatus] })), local: false, pendingCount: 0 });
  }
  for (const entry of pending) {
    const c = entry.command;
    if (c.type === "order.create" && !views.has(c.orderId)) {
      const now = entry.createdAt;
      views.set(c.orderId, {
        id: c.orderId,
        locationId: "",
        number: 0,
        type: c.orderType,
        status: "open",
        mergedInto: null,
        fiscalStatus: "not_required",
        version: 0,
        tableId: c.tableId ?? null,
        tableName: null,
        waiterId: null,
        waiterName: null,
        guests: c.guests ?? null,
        note: c.note ?? null,
        discount: null,
        items: [],
        payments: [],
        totals: { subtotal: 0, discountTotal: 0, total: 0, paid: 0, due: 0 },
        createdAt: now,
        updatedAt: now,
        local: true,
        pendingCount: 1,
      });
    } else if (c.type === "order.add_items") {
      const v = views.get(c.orderId);
      if (!v) continue;
      const known = new Set(v.items.map((i) => i.id));
      for (const input of c.items) if (!known.has(input.itemId)) v.items.push(itemFromInput(input, catalog));
      v.pendingCount += 1;
    } else if ("orderId" in c && views.has(c.orderId)) {
      views.get(c.orderId)!.pendingCount += 1;
    }
  }
  for (const v of views.values()) {
    if (v.pendingCount === 0) continue;
    const t = computeTotals(
      v.items.map((i) => ({ ...i, voided: i.kitchenStatus === "voided" })),
      v.discount,
    );
    v.totals = { ...v.totals, subtotal: t.subtotal, discountTotal: t.discountTotal, total: t.total, due: Math.max(0, t.total - v.totals.paid) };
  }
  return [...views.values()];
}
